import { createSignal } from "solid-js";
import type { TokensByLine, LangId, ThemeId, Timed } from "@fastdiff/highlight";

export type HighlightStatus = "idle" | "loading" | "ready" | "error";

export interface HighlightDriver {
  tokenize(text: string, lang: LangId, theme: ThemeId, signal?: AbortSignal): Promise<Timed<TokensByLine>>;
}

export interface HighlightTiming {
  leftMs: number;
  rightMs: number;
  maxMs: number;
}

export interface HighlightStore {
  status: () => HighlightStatus;
  left: () => TokensByLine | null;
  right: () => TokensByLine | null;
  leftLang: () => LangId;
  rightLang: () => LangId;
  tokenize: (leftText: string, rightText: string, langL: LangId, langR: LangId, theme: ThemeId) => Promise<void>;
  reset: () => void;
  timing: () => HighlightTiming | null;
  grammarLoadMs: () => number | null;
  setGrammarLoadMs: (ms: number) => void;
}

export function createHighlightStore(driver: HighlightDriver): HighlightStore {
  const [status, setStatus] = createSignal<HighlightStatus>("idle");
  const [left, setLeft] = createSignal<TokensByLine | null>(null);
  const [right, setRight] = createSignal<TokensByLine | null>(null);
  const [leftLang, setLeftLang] = createSignal<LangId>("plaintext");
  const [rightLang, setRightLang] = createSignal<LangId>("plaintext");
  const [timing, setTiming] = createSignal<HighlightTiming | null>(null);
  const [grammarLoadMs, setGrammarLoadMsSig] = createSignal<number | null>(null);

  let activeController: AbortController | null = null;

  async function tokenize(
    leftText: string,
    rightText: string,
    langL: LangId,
    langR: LangId,
    theme: ThemeId,
  ): Promise<void> {
    if (activeController) activeController.abort();
    const ctrl = new AbortController();
    activeController = ctrl;

    setStatus("loading");
    setLeft(null);
    setRight(null);
    setLeftLang(langL);
    setRightLang(langR);

    let leftMs = 0;
    let rightMs = 0;

    const lp = driver.tokenize(leftText, langL, theme, ctrl.signal).then((t) => {
      if (ctrl.signal.aborted) return;
      setLeft(t.result);
      leftMs = t.timingMs;
    });
    const rp = driver.tokenize(rightText, langR, theme, ctrl.signal).then((t) => {
      if (ctrl.signal.aborted) return;
      setRight(t.result);
      rightMs = t.timingMs;
    });

    try {
      await Promise.all([lp, rp]);
      if (!ctrl.signal.aborted) {
        setTiming({
          leftMs,
          rightMs,
          maxMs: Math.max(leftMs, rightMs),
        });
        setStatus("ready");
      }
    } catch (e) {
      if (!ctrl.signal.aborted) setStatus("error");
      throw e;
    } finally {
      if (activeController === ctrl) activeController = null;
    }
  }

  function reset(): void {
    if (activeController) activeController.abort();
    activeController = null;
    setStatus("idle");
    setLeft(null);
    setRight(null);
    setLeftLang("plaintext");
    setRightLang("plaintext");
    setTiming(null);
    setGrammarLoadMsSig(null);
  }

  function setGrammarLoadMs(ms: number): void {
    setGrammarLoadMsSig(ms);
  }

  return { status, left, right, leftLang, rightLang, tokenize, reset, timing, grammarLoadMs, setGrammarLoadMs };
}
